/**
 * Exact proportional allocation of an integer amount across integer weights.
 *
 * This exists for one reason: a whole-invoice discount CANNOT be subtracted
 * from the document total, because the lines carry different GST rates (18%
 * fabrication, 5%/12%/18% works contract — spec §8.1). ₹1,000 off a document
 * holding an 18% line and a 5% line must reduce each line's taxable value by
 * its own share, and each line's tax is then recomputed at its own rate. Take
 * it off the total instead and both the CGST and the SGST figure printed on a
 * legal tax invoice are wrong.
 *
 * Proportional allocation is division, so it rounds, so the parts do not add
 * up on their own. Making them add up EXACTLY is the whole content of this
 * file.
 */
import { assertInteger, assertNonNegativeInteger, mulExact } from "./rounding.js";
/**
 * Floor division with the exact remainder, for a non-negative numerator and a
 * positive denominator.
 *
 * Deliberately NOT `divRoundHalfUp`. That is the engine's rounding rule for a
 * *single* quotient, and it is the right rule there — but it is the wrong tool
 * for splitting one amount into parts, because half-up parts can sum to MORE
 * than the amount being split. Split 2 paise across three equal lines:
 * `divRoundHalfUp(2 * 1, 3) === 1` for each, summing to 3 — one paisa of
 * discount the owner never gave. Recovering from that means taking a paisa
 * back off some arbitrarily chosen line, which is unjustifiable on a tax
 * document.
 *
 * Floors can only ever undershoot, never overshoot: the shortfall is exactly
 * the sum of the dropped fractions, which is a whole number strictly less than
 * the number of lines, and it is handed back out below. Every paisa is
 * accounted for and no paisa is invented.
 *
 * Both intermediate products are bounded by the numerator, which the caller has
 * already forced through {@link mulExact}, so the integer arithmetic is exact.
 * The correction loops mirror `divRoundHalfUp` and repair any drift in the
 * initial floating-point estimate.
 */
function floorDivMod(numerator, denominator) {
    let quotient = Math.floor(numerator / denominator);
    let remainder = numerator - quotient * denominator;
    while (remainder < 0) {
        quotient -= 1;
        remainder += denominator;
    }
    while (remainder >= denominator) {
        quotient += 1;
        remainder -= denominator;
    }
    return { quotient, remainder };
}
/**
 * Splits `total` across `weights` in proportion, using the **largest-remainder
 * (Hamilton) method**, and returns shares that sum to `total` EXACTLY.
 *
 * ## Why largest-remainder, and not "give the drift to the biggest line"
 *
 * Both make the parts sum. Largest-remainder is chosen because it is the only
 * one of the two that keeps EVERY share within one paisa of its exact
 * proportional value. Dumping the whole shortfall on one line lets that line
 * drift by up to (lines − 1) paise from its true share — on a ten-line invoice
 * the biggest line silently absorbs up to 9 paise that proportionally belong to
 * the others, and since each line's tax is computed on its own share, that
 * misplaces tax between rates too. Largest-remainder also has no systematic
 * bias: it hands the leftover to the lines whose fractions were closest to
 * being rounded up anyway, rather than always to the same (largest) line, so
 * the error does not accumulate in one place across a run of invoices.
 *
 * It is also the method a CA will recognise on sight, which matters when the
 * question is "why is this line 47.33 and not 47.34".
 *
 * ## Tie-break
 *
 * Equal remainders are settled by **earliest line first**, so the result is a
 * deterministic function of the inputs and their order. Three equal lines
 * sharing 2 paise always yield `[1, 1, 0]`, never a different permutation
 * between the live preview and the saved invoice.
 *
 * ## Guarantees (all asserted below and covered by tests)
 *
 * - `sum(result) === total`, exactly, for every input.
 * - `0 <= result[i] <= weights[i]` when `total <= sum(weights)`, so subtracting
 *   a share from its line can never drive a taxable value negative. Proof: the
 *   floor is at most `weights[i]`, and it can only reach `weights[i]` when
 *   `total === sum(weights)` — in which case every remainder is zero, the
 *   leftover is zero, and no line receives the +1.
 * - A zero weight always receives zero. Its remainder is zero, and the count of
 *   non-zero remainders is always strictly greater than the leftover, so a zero
 *   remainder is never inside the top `leftover`.
 *
 * @param total non-negative integer paise to distribute.
 * @param weights non-negative integers; typically each line's taxable paise.
 * @throws when `total` exceeds `sum(weights)`, when any input is not a
 *   non-negative safe integer, or when a positive `total` is asked to spread
 *   over weights that sum to zero.
 */
export function apportionByWeights(total, weights) {
    assertNonNegativeInteger(total, "apportionByWeights total");
    if (!Array.isArray(weights)) {
        throw new Error("apportionByWeights: weights must be an array");
    }
    weights.forEach((weight, index) => {
        assertNonNegativeInteger(weight, `apportionByWeights weight[${index}]`);
    });
    let weightSum = 0;
    for (const weight of weights) {
        weightSum += weight;
    }
    assertInteger(weightSum, "apportionByWeights weight sum");
    if (total > weightSum) {
        throw new Error(`apportionByWeights: cannot distribute ${total} across weights summing to only ${weightSum}`);
    }
    if (total === 0) {
        return weights.map(() => 0);
    }
    // total > 0 and total <= weightSum, so weightSum > 0 here. Guarded anyway,
    // because a silent divide-by-zero is exactly the sort of thing that reaches
    // a printed invoice.
    if (weightSum === 0) {
        throw new Error("apportionByWeights: cannot distribute a positive total across zero total weight");
    }
    const shares = new Array(weights.length);
    const remainders = [];
    let allocated = 0;
    for (let index = 0; index < weights.length; index += 1) {
        const numerator = mulExact(total, weights[index], `apportionByWeights share[${index}]`);
        const { quotient, remainder } = floorDivMod(numerator, weightSum);
        shares[index] = quotient;
        allocated += quotient;
        remainders.push({ index, remainder });
    }
    // The shortfall is a whole number of paise, strictly less than the number of
    // weights. Hand one paisa each to the largest remainders, earliest first.
    const leftover = total - allocated;
    if (leftover < 0 || leftover >= Math.max(weights.length, 1)) {
        throw new Error(`apportionByWeights: internal invariant broken — leftover ${leftover} out of range for ${weights.length} weights`);
    }
    remainders.sort((a, b) => (b.remainder - a.remainder) || (a.index - b.index));
    for (let taken = 0; taken < leftover; taken += 1) {
        const target = remainders[taken];
        shares[target.index] = shares[target.index] + 1;
    }
    // Postconditions. Cheap, and they turn a silent one-paisa defect — the kind
    // the CA finds and nobody can explain — into a loud failure.
    let check = 0;
    for (let index = 0; index < shares.length; index += 1) {
        const share = shares[index];
        if (share < 0 || share > weights[index]) {
            throw new Error(`apportionByWeights: internal invariant broken — share ${share} at index ${index} is outside [0, ${weights[index]}]`);
        }
        check += share;
    }
    if (check !== total) {
        throw new Error(`apportionByWeights: internal invariant broken — shares sum to ${check}, expected ${total}`);
    }
    return shares;
}
//# sourceMappingURL=apportion.js.map